import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Tab, Tabs, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from '../../../../components/PageHeader';
import { unusedMeds1 } from '../../../../icons';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getPharmaceuticalWaste } from '../../../../services/wasteManagementService';
import { PharmaceuticalWasteItem, WasteStatus } from '../types';
import constants from '../../../../constants';
import { ComplianceWrapper } from '../components/ComplianceWrapper';
import pharmaceuticalWasteContainersImage from '../../../../images/items/pharmaceutical_waste_container.png';
import { sendEmail } from '../../../../services/emailService';
import { selectCurrentUser } from '../../../../redux/user';
import {
  getSubscriptionData,
  selectAutoShipmentItems,
  selectLoadingState,
  selectSubscriptionCustomerNumber,
  selectSubscriptionItems,
  selectTimeBasedTbrItems
} from '../../../../redux/subscription';
import PharmaceuticalWasteContainer from './PharmaceuticalWasteContainer';
import { addError, addSuccess } from '../../../../redux/alerts';
import { useNavigate } from 'react-router-dom';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import useIsCorpView from '../../../../hooks/useIsCorpView';

const initialCompliance = {
  message_long: '',
  compliance_status: 4
};

const PharmaceuticalWaste = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isCorpView = useIsCorpView()
  const currentUser = useAppSelector(selectCurrentUser);
  const tbrItems = useAppSelector(selectTimeBasedTbrItems);
  const subscriptionCustomerNumber = useAppSelector(selectSubscriptionCustomerNumber);
  const loadingState = useAppSelector(selectLoadingState);
  const subscriptionItems = useAppSelector(selectSubscriptionItems);
  const autoShipmentItems = useAppSelector(selectAutoShipmentItems);

  const [pharmaceuticalWaste, setPharmaceuticalWaste] = useState<PharmaceuticalWasteItem[]>([]);
  const [pharmaceuticalWasteCompliance, setPharmaceuticalWasteCompliance] = useState(initialCompliance);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);

  const { hasEligibleAutoShipments, hasAutoShipments } = useMemo(() => {
    if (!pharmaceuticalWaste.length || (!tbrItems.length && !subscriptionItems.length)) {
      return { hasEligibleAutoShipments: false, hasAutoShipments: false }
    }

    const pharmaceuticalWasteIds = new Set(
      pharmaceuticalWaste.map((item) => item.item_number.trim())
    )
    const subscriptionIds = subscriptionItems.map((item) => item.itemId.trim())
    const subscriptionMatches = subscriptionIds.filter((id) => pharmaceuticalWasteIds.has(id))

    if (subscriptionMatches.length) {
      return { hasEligibleAutoShipments: true, hasAutoShipments: true }
    }

    if (tbrItems.length) {
      const tbrEligibleIds = new Set(
        tbrItems.map((item) => item.ItemNumber.trim())
      )
      const tbrItemMatches = Array.from(pharmaceuticalWasteIds).filter((id) => tbrEligibleIds.has(id))

      if (tbrItemMatches.length) {
        return { hasEligibleAutoShipments: true, hasAutoShipments: false }
      }
    }

    return { hasEligibleAutoShipments: false, hasAutoShipments: false }
  }, [pharmaceuticalWaste, tbrItems, subscriptionItems])

  const customerNumber = useCustomerNumber()

  const isRenewal = (item: PharmaceuticalWasteItem) => {
    if (!autoShipmentItems) return false;

    return !!autoShipmentItems.find((autoShipment) => parseInt(autoShipment.itemId, 10) === parseInt(item.item_number, 10));
  };

  const getPharmaceuticalWasteData = async () => {
    const { pharma_containers, practice_pharma_compliance } = await getPharmaceuticalWaste(customerNumber)

    if (practice_pharma_compliance?.compliance) {
      setPharmaceuticalWasteCompliance({
        message_long: practice_pharma_compliance?.compliance.message_long,
        compliance_status: practice_pharma_compliance?.compliance.compliance_status
      });
    }

    const pharmaList = pharma_containers.map((item: PharmaceuticalWasteItem) => ({ ...item, isRenewal: isRenewal(item) }));

    setPharmaceuticalWaste(pharmaList);
  };

  const sendReorderEmail = async () => {
    if (!currentUser) return;
    setEmailLoading(true);
    
    try {
      const emailDetails = {
        firstname: currentUser.firstname,
        lastname: currentUser.lastname,
        userEmail: currentUser.email,
        notificationName: 'pharmaContainerReorder'
      }
      await sendEmail(emailDetails, customerNumber);
      setEmailLoading(false);
      dispatch(addSuccess('Your request has been sent to one of our compliance managers. You will be contacted shortly.'));
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem sending your request, please try again later'));
      setEmailLoading(false);
    }
  };

  const getInUseItems = useMemo(() => {
    return pharmaceuticalWaste
      .filter((item) => item.used_date === null && item.processed_date === null)
      .map((item) => ({ ...item, disposal_status: WasteStatus.inUse }))
      .sort((a, b) => (a.shipped_date < b.shipped_date ? -1 : a.shipped_date > b.shipped_date ? 1 : 0));
  }, [pharmaceuticalWaste]);

  const getUsedItems = useMemo(() => {
    return pharmaceuticalWaste
      .filter((item) => item.used_date !== null && item.processed_date === null)
      .map((item) => ({ ...item, disposal_status: WasteStatus.used }))
      .sort((a, b) => {
        if (!a.used_date && !b.used_date) return 0;
        if (!a.used_date) return 1;
        if (!b.used_date) return -1;

        return a.used_date > b.used_date ? -1 : a.used_date < b.used_date ? 1 : 0;
      });
  }, [pharmaceuticalWaste]);

  const getProcessedItems = useMemo(() => {
    return pharmaceuticalWaste
      .filter((item) => item.processed_date !== null)
      .map((item) => ({ ...item, disposal_status: WasteStatus.destroyed }))
      .sort((a, b) => (a.processed_date > b.processed_date ? -1 : a.processed_date < b.processed_date ? 1 : 0));
  }, [pharmaceuticalWaste]);

  useEffect(() => {
    if (customerNumber) {
      getPharmaceuticalWasteData();

      if (loadingState === 'not-asked' || subscriptionCustomerNumber !== customerNumber) {
        dispatch(getSubscriptionData(customerNumber));
      }
    }
  }, [customerNumber]);

  const pageTabs = [`In Use(${getInUseItems.length})`, `Used(${getUsedItems.length})`, `Processed(${getProcessedItems.length})`];

  const AutoShipmentButton = () => {
    if (hasEligibleAutoShipments) {
      const link = isCorpView
        ? `/corp/practice/${customerNumber}/auto-renewals`
        : `/practice/auto-renewals`;
      return (
        <div className="flex flex-end m-b-16 m-r-16">
          {hasAutoShipments ? (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <EditIcon fontSize="small" /> Edit Auto Shipments
            </Button>
          ) : (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <AddIcon fontSize="small" /> Enroll in Auto Shipments
            </Button>
          )}
        </div>
      );
    }

    return;
  };

  const TabContent = () => {
    const tabContents = [
      <Box key="0" sx={{ p: '0 16px 16px 16px' }}>
        <div className="flex flex-center flex-align-center m-b-16">
          <img style={{ width: '125px' }} className="m-r-16" src={pharmaceuticalWasteContainersImage} alt="Pharmaceutical Waste Containers" />
          <div className="flex flex-column">
            {getInUseItems.length === 0 && (
              <div className="f-s-23 m-b-8">Your office is currently out of Pharmaceutical containers. Reorder now.</div>
            )}
            {getInUseItems.length === 1 && <div className="f-s-23 m-b-8">Only 1 Pharmaceutical container is left in your office. Reorder now.</div>}
            <div className="center">
              <Button onClick={sendReorderEmail} variant="contained" disabled={emailLoading}>
                Reorder Pharmaceutical Containers
              </Button>
            </div>
          </div>
        </div>
        {getInUseItems.length > 0 &&
          getInUseItems.map((item) => (
            <PharmaceuticalWasteContainer
              item={item}
              key={item.container_id}
              getWasteData={getPharmaceuticalWasteData}
            />
          ))}
      </Box>,
      <Box key="1" sx={{ p: '0 16px 16px 16px' }}>
        {getUsedItems.length
          ? getUsedItems.map((item) => (
            <PharmaceuticalWasteContainer
              item={item}
              key={item.container_id}
              getWasteData={getPharmaceuticalWasteData}
            />
          ))
          : 'There are no used items'}
      </Box>,
      <Box key="2" sx={{ p: '0 16px 16px 16px' }}>
        {getProcessedItems.length
          ? getProcessedItems.map((item) => (
            <PharmaceuticalWasteContainer
              item={item}
              key={item.container_id}
              getWasteData={getPharmaceuticalWasteData}
            />
          ))
          : 'There are no processed items'}
      </Box>
    ];

    return tabContents[tabIndex];
  };

  return (
    <>
      <PageHeader src={unusedMeds1} headerName={'Pharmaceutical Waste'} size={'26px'} />
      {pharmaceuticalWaste.length === 0 ? (
        <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.PHARMACEUTICAL_WASTE_SALES_PAGE_URL} />
      ) : (
        <>
          <ComplianceWrapper compliance={pharmaceuticalWasteCompliance} />
          <Box sx={{ color: theme.palette.color50555b.main }}>
            <Box>
              <AutoShipmentButton />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  href={constants.RP_RETURNS_URL}
                  target="_blank"
                  rel="noopener"
                  sx={{ width: '500px', textAlign: 'center' }}
                >
                  To view your itemized report of materials received for processing related to your controlled substances, click here to log in to
                  your RP Returns account
                </Button>
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end m-b-16">
                <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)}>
                  {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
                </Tabs>
              </Box>
              <TabContent />
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default PharmaceuticalWaste;
